import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  CheckCircle, ArrowRight, X, Sparkle, CircleNotch,
  Wrench, ChatTeardropText, Brain, Megaphone, Pulse, PlugsConnected,
} from "@phosphor-icons/react";

/**
 * SetupWizard — "Setup em 5 minutos" banner that sits above the
 * Operação tabs and guides a fresh tenant through the 6 critical
 * activation steps. Each step maps either to an internal /operacao
 * tab (via hash) or to an external route (offers, automations,
 * integrations). The wizard auto-hides when setup_health hits 100%.
 *
 * UX rules:
 *  - Auto-shows for any tenant where dashboard/setup_health is < 6/6.
 *  - Persistent dismissal via localStorage, but the flag is keyed by
 *    `done/total` so completing a step resets the dismissal (the
 *    operator sees the next nudge naturally).
 *  - Last step completion shows a 1.4s celebration row before fading.
 */

// Visual & navigation metadata for each backend setup_health item.
// We pin icon+blurb here so the backend stays purely about state.
const STEP_META = {
  services:   { icon: Wrench,          tab: "services",   external: null,            blurb: "Pra IA cotar com precisão." },
  messages:   { icon: ChatTeardropText, tab: "messages", external: null,            blurb: "Define o tom de boas-vindas." },
  knowledge:  { icon: Brain,           tab: "knowledge",  external: null,            blurb: "Tudo que a IA precisa saber — contratos, FAQ, políticas, cartilhas." },
  offers:     { icon: Megaphone,       tab: "promotions", external: null,            blurb: "Ofertas que a IA propõe na hora." },
  rules:      { icon: Pulse,           tab: null,         external: "/app/decisao",  blurb: "Lembretes e reativações." },
  whatsapp:   { icon: PlugsConnected,  tab: null,         external: "/app/integracoes", blurb: "Conecta o WhatsApp real." },
};

const STORAGE_PREFIX = "agentus_setup_wizard_hidden_v1";

function StepRow({ item, onGo, isActive }) {
  const meta = STEP_META[item.id] || { icon: Sparkle, blurb: "" };
  const Icon = meta.icon;
  const done = item.status === "done";
  // WhatsApp é o gargalo único — sem ele, nada do que o operador
  // configurar pode ser testado. Marcamos com um "tag" sutil em vez
  // de cores quentes para preservar a estética monocromática.
  const isCritical = !done && item.id === "whatsapp";
  return (
    <button
      type="button"
      data-testid={`wizard-step-${item.id}`}
      onClick={onGo}
      disabled={done}
      className={`group w-full text-left px-4 py-3 rounded-xl border transition-all ${
        done
          ? "border-slate-100 bg-slate-50/60 cursor-default"
          : isCritical
          ? "border-slate-300 bg-white hover:border-slate-900 hover:shadow-sm"
          : "border-slate-200 hover:border-slate-900 hover:shadow-sm bg-white"
      } ${isActive ? "ring-1 ring-slate-900" : ""}`}
    >
      <div className="flex items-center gap-3">
        {/* Step icon — substitui o círculo vazio. Quando concluído mostra
            o check em escala slate; pendente mostra a categoria. */}
        <div className={`shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
          done
            ? "bg-slate-900 text-white"
            : "bg-slate-100 text-slate-700 group-hover:bg-slate-900 group-hover:text-white"
        }`}>
          {done ? <CheckCircle size={14} weight="fill" /> : <Icon size={14} weight="regular" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-[13px] font-medium tracking-tight truncate ${
              done ? "text-slate-400 line-through" : "text-slate-900"
            }`}>
              {item.label}
            </span>
            {isCritical && (
              <span className="shrink-0 text-[9.5px] uppercase tracking-[0.08em] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">
                obrigatório
              </span>
            )}
          </div>
          {!done && (
            <p className="text-[11.5px] mt-0.5 line-clamp-1 text-slate-500">
              {isCritical
                ? "Sem isso a IA NÃO responde — nem em modo teste."
                : meta.blurb}
            </p>
          )}
        </div>
        {!done && (
          <span
            className={`shrink-0 text-[11px] inline-flex items-center gap-1 transition-opacity ${
              isCritical
                ? "text-slate-700 group-hover:text-slate-900 opacity-100"
                : "text-slate-500 group-hover:text-slate-900 opacity-0 group-hover:opacity-100"
            }`}
            data-testid={`wizard-go-${item.id}`}
          >
            {isCritical ? "Conectar agora" : "Ir agora"} <ArrowRight size={11} weight="bold" />
          </span>
        )}
      </div>
    </button>
  );
}

export default function SetupWizard({ onNavigateTab, autoFadeOnComplete = true }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/dashboard/setup_health");
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Re-check setup status every time the operator finishes a tab save.
  // We piggyback on the page's `hashchange` event because every tab
  // change triggers it — cheap and bound-already.
  useEffect(() => {
    const onHash = () => { load(); };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const storageKey = useMemo(
    () => data ? `${STORAGE_PREFIX}_${data.done}_${data.total}` : null,
    [data]
  );

  // The dismissal flag is keyed by current progress so completing a
  // step naturally re-surfaces the wizard for the next nudge.
  useEffect(() => {
    if (!storageKey) return;
    try {
      setHidden(localStorage.getItem(storageKey) === "1");
    } catch { /* ignore */ }
  }, [storageKey]);

  // When all 6 done — celebrate (visual ring + congratulatory copy)
  // then auto-fade. The fade-timer lives in its OWN effect so the
  // dep change (celebrating false→true) doesn't trigger the cleanup
  // and cancel the pending setTimeout.
  useEffect(() => {
    if (!data) return;
    if (data.done === data.total && data.total > 0 && !celebrating && !hidden) {
      setCelebrating(true);
    }
  }, [data, celebrating, hidden]);

  useEffect(() => {
    if (!celebrating || !autoFadeOnComplete) return;
    const t = setTimeout(() => {
      // Persist the "completed & dismissed" state so a page reload
      // doesn't bring the wizard back. Without this, the wizard would
      // re-appear forever once setup hits 6/6 because the dismissal
      // only lived in component memory.
      try {
        if (storageKey) localStorage.setItem(storageKey, "1");
      } catch { /* ignore */ }
      setHidden(true);
    }, 3000);  // 3s — long enough to acknowledge success, short enough to clear way for actual operational content.
    return () => clearTimeout(t);
  }, [celebrating, autoFadeOnComplete, storageKey]);

  if (loading || !data) return null;
  if (data.total === 0) return null;
  if (hidden && data.done !== data.total) return null;
  if (hidden && data.done === data.total) return null;

  const items = data.items || [];
  const pct = Math.round((data.done / data.total) * 100);

  const handleGo = (it) => {
    setActiveId(it.id);
    // ── UX (v0.89): when the operator picks a setup step, the wizard
    // gets in the way of the very content they want to focus on.
    // Hide it for the rest of THIS session so the destination screen
    // breathes; on next page reload the wizard returns automatically
    // and reflects fresh progress.
    setHidden(true);
    const meta = STEP_META[it.id];
    if (meta?.tab) {
      // Internal tab — use callback if running inside /operacao, else
      // navigate via the deep-link hash so it works from Dashboard too.
      if (onNavigateTab) {
        onNavigateTab(meta.tab);
      } else {
        navigate(`/app/operacao#${meta.tab}`);
      }
    } else if (meta?.external) {
      navigate(meta.external);
    } else if (it.cta_path) {
      // Fallback to whatever the backend tells us.
      if (it.cta_path.startsWith("/operacao#")) {
        const tab = it.cta_path.split("#")[1];
        if (onNavigateTab) onNavigateTab(tab);
        else navigate(`/app/operacao#${tab}`);
      } else {
        navigate(`/app${it.cta_path}`);
      }
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    setHidden(true);
  };

  return (
    <section
      data-testid="setup-wizard"
      className={`mb-6 rounded-2xl border border-slate-200/70 bg-white overflow-hidden transition-opacity duration-500 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${
        celebrating ? "ring-1 ring-slate-900" : ""
      }`}
    >
      {/* Header */}
      <header className="px-5 py-4 flex items-center justify-between gap-4 border-b border-slate-100">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0">
            <Sparkle size={13} weight="fill" />
          </div>
          <div className="min-w-0">
            <h3 className="text-[13.5px] font-semibold tracking-tight text-slate-900 truncate">
              {celebrating ? "Tudo pronto." : "Setup em 5 minutos"}
            </h3>
            <p className="text-[11.5px] text-slate-500 truncate">
              {celebrating
                ? "Sua IA está pronta pra atender com precisão."
                : "Conclua estes passos pra IA parar de improvisar."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4 shrink-0">
          <div className="hidden sm:flex items-center gap-2.5">
            <div className="w-28 h-[3px] rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-slate-900 transition-all duration-500"
                style={{ width: `${pct}%` }}
                data-testid="wizard-progress-bar"
              />
            </div>
            <span className="text-[11.5px] tabular-nums text-slate-600 font-medium" data-testid="wizard-progress-text">
              {data.done}/{data.total}
            </span>
          </div>
          {data.done < data.total && (
            <button
              type="button"
              data-testid="wizard-dismiss"
              onClick={dismiss}
              className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
              title="Mostrar depois"
              aria-label="Mostrar depois"
            >
              <X size={11} weight="bold" />
            </button>
          )}
          {data.done === data.total && !autoFadeOnComplete && (
            <button
              type="button"
              data-testid="wizard-dismiss-complete"
              onClick={dismiss}
              className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
              title="Fechar"
              aria-label="Fechar"
            >
              <X size={11} weight="bold" />
            </button>
          )}
        </div>
      </header>

      {/* Body */}
      {!celebrating && (
        <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="wizard-steps">
          {items.map((it) => (
            <StepRow
              key={it.id}
              item={it}
              onGo={() => handleGo(it)}
              isActive={activeId === it.id}
            />
          ))}
        </div>
      )}

      {celebrating && (
        <div className="px-5 py-6 flex items-center gap-3" data-testid="wizard-celebration">
          <CheckCircle size={28} weight="fill" className="text-slate-900" />
          <div>
            <div className="text-sm font-semibold text-slate-900">6 de 6 concluído</div>
            <div className="text-[12px] text-slate-500">O Cérebro some daqui em segundos. Bom atendimento!</div>
          </div>
        </div>
      )}
    </section>
  );
}
